/**
 * AI Room Creator + Room Manager
 * Admin creates rooms via AI description, manages existing custom rooms
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Plus, Trash2, Sparkles, Loader2, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../utils/tokenStorage';

const RoomCreator = ({ isOpen, onClose, onRoomCreated }) => {
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [customRooms, setCustomRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) fetchRooms();
  }, [isOpen]);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/nightclub/custom-rooms`);
      if (res.ok) {
        const rooms = await res.json();
        setCustomRooms(rooms);
      }
    } catch (e) {
      console.error('Failed to fetch rooms');
    }
    setLoading(false);
  };

  const createRoom = async () => {
    if (!description.trim() || creating) return;
    setCreating(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/nightclub/ai/create-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ description: description.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setDescription('');
        fetchRooms();
        onRoomCreated?.(data.room);
      } else {
        toast.error(data.detail || 'Failed to create room');
      }
    } catch (e) {
      toast.error('Failed to create room');
    }
    setCreating(false);
  };

  const deleteRoom = async (roomId) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/nightclub/custom-rooms/${roomId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Room deleted');
        fetchRooms();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (e) {
      toast.error('Failed to delete room');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      data-testid="room-creator-overlay"
    >
      <Card
        className="w-full max-w-md max-h-[80vh] overflow-y-auto bg-gray-950 border-cyan-500/30"
        onClick={e => e.stopPropagation()}
        data-testid="room-creator-modal"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              AI Room Creator
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-room-creator">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-white/40 text-xs">Describe a room and AI will design it</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Create Form */}
          <div className="space-y-2">
            <Input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g., Underwater cave with glowing jellyfish and bioluminescent coral..."
              className="bg-gray-800 border-cyan-500/20 text-white"
              disabled={creating}
              data-testid="room-description-input"
            />
            <Button
              onClick={createRoom}
              disabled={creating || !description.trim()}
              className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500"
              data-testid="create-room-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  AI is designing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create with AI
                </>
              )}
            </Button>
          </div>

          {/* Existing Custom Rooms */}
          <div className="space-y-2">
            <h3 className="text-white/60 text-xs uppercase tracking-wider">Custom Rooms ({customRooms.length})</h3>
            {loading ? (
              <div className="text-center py-4 text-white/30">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : customRooms.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-4">No custom rooms yet. Create one above!</p>
            ) : (
              customRooms.map(room => (
                <div
                  key={room.room_id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/50 border border-gray-700/50 group"
                  data-testid={`custom-room-${room.room_id}`}
                >
                  {/* Color preview */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden grid grid-cols-2 grid-rows-2">
                    <div style={{ backgroundColor: room.theme?.bgColor || '#1a1a2e' }} />
                    <div style={{ backgroundColor: room.theme?.wallColor || '#16213e' }} />
                    <div style={{ backgroundColor: room.theme?.floorColor || '#0f3460' }} />
                    <div style={{ backgroundColor: room.theme?.accentColor || '#e94560' }} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium flex items-center gap-1.5">
                      {room.name}
                      {room.ai_generated && (
                        <span className="text-[9px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded-full">AI</span>
                      )}
                    </div>
                    <div className="text-white/40 text-[10px] truncate">{room.description}</div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteRoom(room.room_id)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-room-${room.room_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoomCreator;
